import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { jsx } from 'react/jsx-runtime';
import { createMockParcel, createMockShop, createMockPickupPoint } from '@babackapp/mocks';

// ../carriers-utils/dist/index.js
var WEEKDAYS = [1, 2, 3, 4, 5];
var WEEKDAYS_SAT = [1, 2, 3, 4, 5, 6];
var MONDIAL_DAYS = [2, 3, 4, 5, 6];
var CARRIER_REGISTRY = {
  colissimo: {
    name: "Colissimo",
    logo: "colissimo.png",
    match: (code) => code.includes("colissimo") || code === "la-poste" || code === "lapost" || code === "la-poste-colissimo" || code === "lapostcolissimo" || code === "lettre-suivie" || code === "lettre suivie" || code === "lettresuivie" || code === "delivengo",
    tracking: { provider: "native", trackingMoreCarrierCode: "colissimo" },
    trackingUrl: {
      url: "https://www.laposte.fr/outils/suivre-vos-envois?code={trackingId}",
      fallbackUrl: "https://www.laposte.fr/outils/suivre-vos-envois"
    },
    matchTrackingNumber: (tn) => /^[68]A\d{9}FR$/i.test(tn) || /^[C-R][A-Z]\d{9}FR$/i.test(tn),
    meta: {
      trackingMethod: "API officielle La Poste v2 (OAuth2) + fallback scraper SmartProxy v1",
      rateLimit: "~5 req/s (429 avec Retry-After)",
      notes: "Circuit breaker 5s sur 429. Token OAuth2 cached avec marge 1min."
    },
    credentials: {
      // Tracking utilise une clé OkApi centrale Baback + fallback scraper SmartProxy ;
      // les identifiants marchand ne servent qu'à la génération d'étiquettes.
      usedFor: ["shipment"],
      supportsCredentialCheck: true,
      fields: [
        { key: "accountId", label: "Account ID", type: "string", required: true },
        { key: "password", label: "Password", type: "string", required: true, sensitive: true },
        { key: "SLS", label: "Suivi Livraison Sign\xE9 (SLS)", type: "boolean", required: false }
      ]
    },
    supportsLabelGeneration: true,
    products: [
      { code: "colissimo", isPickupPoint: false, name: "Colissimo", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "DOM", international: "COLD" }, servedZones: ["FR_METRO", "EU", "OM1", "OM2", "MONDE"], zoneDelays: { EU: { promise: 4, padding: 2 }, OM1: { promise: 8, padding: 3 }, OM2: { promise: 10, padding: 4 }, MONDE: { promise: 7, padding: 2 } } },
      { code: "livraison-domicile", isPickupPoint: false, name: "Livraison \xE0 domicile", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "DOM", international: "COLD" }, servedZones: ["FR_METRO", "EU", "OM1", "OM2", "MONDE"], zoneDelays: { EU: { promise: 4, padding: 2 }, OM1: { promise: 8, padding: 3 }, OM2: { promise: 10, padding: 4 }, MONDE: { promise: 7, padding: 2 } } },
      { code: "point-retrait", isPickupPoint: true, name: "Point retrait", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "A2P" }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5 } } },
      { code: "colissimo_domicile_signature_france", isPickupPoint: false, name: "Colissimo Domicile Signature - France", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "DOS" }, servedZones: ["FR_METRO"] },
      { code: "colissimo_domicile_sans_signature_france", isPickupPoint: false, name: "Colissimo Domicile Sans Signature - France", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "DOM" }, servedZones: ["FR_METRO"] },
      { code: "colissimo_point_retrait_france", isPickupPoint: true, name: "Colissimo Point Retrait - France", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "A2P" }, servedZones: ["FR_METRO"] },
      // MONDE recalé sur mesure (159 colis) : le produit est utilisé hors EU, où il met 5 à 9 jours.
      { code: "colissimo_domicile_signature_europe", isPickupPoint: false, name: "Colissimo Domicile Signature - Europe", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "COLI" }, servedZones: ["EU"], zoneDelays: { MONDE: { promise: 5, padding: 4 } } },
      { code: "colissimo_domicile_sans_signature_europe", isPickupPoint: false, name: "Colissimo Domicile Sans Signature - Europe", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "COLD" }, servedZones: ["EU"] },
      { code: "colissimo_point_retrait_europe", isPickupPoint: true, name: "Colissimo Point Retrait - Europe", defaultSmartDelivery: { defaultPromise: 7, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "A2P" }, servedZones: ["EU"] },
      { code: "colissimo_domicile_signature_outre_mer", isPickupPoint: false, name: "Colissimo Domicile Signature - Outre-Mer", defaultSmartDelivery: { defaultPromise: 7, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "COLI" }, servedZones: ["OM1", "OM2"], zoneDelays: { OM2: { promise: 10, padding: 4 } } },
      { code: "colissimo_domicile_sans_signature_outre_mer", isPickupPoint: false, name: "Colissimo Domicile Sans Signature - Outre-Mer", defaultSmartDelivery: { defaultPromise: 7, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "COLD" }, servedZones: ["OM1", "OM2"], zoneDelays: { OM2: { promise: 10, padding: 4 } } },
      { code: "colissimo_domicile_signature_monde", isPickupPoint: false, name: "Colissimo Domicile Signature - Monde", defaultSmartDelivery: { defaultPromise: 7, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "COLI" }, servedZones: ["MONDE"] },
      { code: "lettre-suivie", isPickupPoint: false, name: "Lettre Suivie", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["FR_METRO"], zoneDelays: { EU: { promise: 5, padding: 2 } } }
    ]
  },
  chronopost: {
    name: "Chronopost",
    logo: "chronopost.png",
    match: (code) => code.startsWith("chrono"),
    tracking: { provider: "native", trackingMoreCarrierCode: "chronopost" },
    trackingUrl: {
      url: "https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT={trackingId}",
      fallbackUrl: "https://www.chronopost.fr/tracking-no-cms/suivi-page"
    },
    matchTrackingNumber: (tn) => /^X[A-Z]\d{9}[A-Z]{2}$/i.test(tn),
    meta: {
      trackingMethod: "Web service SOAP public Chronopost (trackSkybillV2, non authentifi\xE9)"
    },
    credentials: {
      // Le suivi passe par un endpoint SOAP public anonyme ; les identifiants
      // marchand ne servent qu'à la génération d'étiquettes.
      usedFor: ["shipment"],
      supportsCredentialCheck: true,
      fields: [
        { key: "accountNumber", label: "Account Number", type: "string", required: true },
        { key: "password", label: "Password", type: "string", required: true, sensitive: true },
        { key: "subAccount", label: "Sub Account", type: "string", required: false }
      ]
    },
    supportsLabelGeneration: true,
    supportsPickupRequest: true,
    products: [
      { code: "chronopost-18", isPickupPoint: false, name: "Chronopost 18", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "18", international: "17" }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 4, padding: 2 } } },
      { code: "chronopost-13", isPickupPoint: false, name: "Chronopost 13", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "13", international: "17" }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 4, padding: 2 } } },
      { code: "relais", isPickupPoint: true, name: "Chrono Relais", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "44" }, servedZones: ["FR_METRO"], zoneDelays: { EU: { promise: 3, padding: 2 } } },
      { code: "shop2shop", isPickupPoint: true, name: "Shop2Shop", defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "2R" }, servedZones: ["FR_METRO"] },
      { code: "chronopost_lendemain_13h", isPickupPoint: false, name: "Lendemain avant 13h", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "13", international: "17" }, servedZones: ["FR_METRO"] },
      { code: "chronopost_lendemain_18h", isPickupPoint: false, name: "Livraison le lendemain avant 18h", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "18", international: "17" }, servedZones: ["FR_METRO"] },
      { code: "chronopost_jour_j", isPickupPoint: false, name: "Livraison le jour m\xEAme", defaultSmartDelivery: { defaultPromise: 0, defaultPadding: 0, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "SD" }, servedZones: ["FR_METRO"] },
      { code: "chronopost_relais_france", isPickupPoint: true, name: "Livraison en relais France", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "44" }, servedZones: ["FR_METRO"] },
      { code: "chronopost_rapide_eco", isPickupPoint: false, name: "Livraison rapide et \xE9conomique vers l'Europe", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "16", international: "16" }, servedZones: ["EU"] },
      { code: "chronopost_express_world", isPickupPoint: false, name: "Livraison Express Monde", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "17", international: "17" }, servedZones: ["EU", "MONDE"], zoneDelays: { MONDE: { promise: 4, padding: 2 } } },
      { code: "chronopost_relais_europe_outre_mer", isPickupPoint: true, name: "Livraison en relais Europe et Outre-Mer", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "49", international: "49" }, servedZones: ["EU", "OM1", "OM2"], zoneDelays: { OM2: { promise: 6, padding: 2 } } },
      { code: "chronopost_shop2shop", isPickupPoint: true, name: "Shop2Shop", defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "2R" }, servedZones: ["FR_METRO"] },
      { code: "chronopost_2shopdirect", isPickupPoint: true, name: "2Shop Direct", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, carrierProductCodes: { default: "T2" }, servedZones: ["FR_METRO"], zoneDelays: { EU: { promise: 4, padding: 2 } } }
    ]
  },
  dhl: {
    name: "DHL",
    logo: "dhl.png",
    match: (code) => code === "dhl" || code.includes("dhl-express") || code.includes("dhlexpress") || code.includes("dhl-economy") || code.includes("dhleconomy") || code.includes("dhl-tax-free") || code.includes("dhltaxfree"),
    tracking: { provider: "native", trackingMoreCarrierCode: "dhl" },
    trackingUrl: {
      url: "https://www.dhl.com/{countryCode}/home/tracking.html?tracking-id={trackingId}",
      fallbackUrl: "https://www.dhl.com/fr-fr/home/tracking.html",
      supportsLocale: true,
      formatCountryCode: (countryCode, locale) => `${countryCode.toLowerCase()}-${locale}`
    },
    matchTrackingNumber: (tn) => /^JJD\d{10,}$/i.test(tn),
    meta: {
      trackingMethod: "API officielle DHL Tracking (API key)",
      notes: "Rate limit g\xE9r\xE9 par quota API key."
    },
    credentials: {
      // Le suivi passe par l'API DHL Tracking (clé centrale) ; les identifiants
      // marchand (MyDHL API Express) ne servent qu'à la génération d'étiquettes.
      usedFor: ["shipment"],
      supportsCredentialCheck: false,
      fields: [
        { key: "webSiteId", label: "Web Site ID (MyDHL API)", type: "string", required: true },
        { key: "securityKey", label: "Security Key (MyDHL API)", type: "string", required: true, sensitive: true },
        { key: "shipperAccountNumber", label: "Num\xE9ro de compte exp\xE9diteur", type: "string", required: true },
        { key: "eori", label: "Num\xE9ro EORI", type: "string", required: false },
        { key: "incoterm", label: "Incoterm (DAP ou DDP, d\xE9faut DAP)", type: "string", required: false }
      ]
    },
    // Génération d'étiquettes DHL Express (MyDHL API). V1 : destinations UE +
    // national uniquement (isCustomsDeclarable = false) — le hors-UE exige une
    // déclaration douanière (lignes produits) que la page de création manuelle
    // ne fournit pas encore.
    supportsLabelGeneration: true,
    products: [
      // carrierProductCodes drives MyDHL API label generation.
      // Express family → `N` (national) / `U` (UE) / `P` (international) ;
      // Economy → `W` (UE) / `H` (international). Le hors-UE construit une
      // déclaration douanière (voir dhl-customs.resolver.ts).
      // Products without codes (e.g. dhl_service_point) are not label-capable.
      { code: "dhl", isPickupPoint: false, name: "DHL", carrierProductCodes: { national: "N", eu: "U", international: "P" }, defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 3, padding: 1 } } },
      { code: "dhl-express", isPickupPoint: false, name: "DHL Express", carrierProductCodes: { national: "N", eu: "U", international: "P" }, defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 4, padding: 2 } } },
      { code: "dhl_express_worldwide", isPickupPoint: false, name: "DHL Express Worldwide", carrierProductCodes: { national: "N", eu: "U", international: "P" }, defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU", "MONDE"], zoneDelays: { EU: { promise: 1, padding: 1 }, MONDE: { promise: 3, padding: 1 } } },
      { code: "dhl_express_domestic", isPickupPoint: false, name: "DHL Express Domestic", carrierProductCodes: { national: "N", eu: "U", international: "P" }, defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO"] },
      { code: "dhl_economy_select", isPickupPoint: false, name: "DHL Economy Select", carrierProductCodes: { default: "W", international: "H" }, defaultSmartDelivery: { defaultPromise: 5, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU", "MONDE"], zoneDelays: { EU: { promise: 3, padding: 1 }, MONDE: { promise: 7, padding: 2 } } },
      { code: "dhl_euroconnect", isPickupPoint: false, name: "DHL Euroconnect", carrierProductCodes: { default: "W", international: "H" }, defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "dhl_parcel_europe", isPickupPoint: false, name: "DHL Parcel Europe", carrierProductCodes: { default: "W", international: "H" }, defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "dhl_parcel_connect", isPickupPoint: false, name: "DHL Parcel Connect", carrierProductCodes: { default: "W", international: "H" }, defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "dhl_service_point", isPickupPoint: true, name: "DHL ServicePoint", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"] }
    ]
  },
  ups: {
    name: "UPS",
    logo: "ups.png",
    match: (code) => code === "ups" || code.includes("ups-standard") || code.includes("upsstandard") || code.includes("ups-saver") || code.includes("upssaver") || code.includes("ups-ground") || code.includes("upsground"),
    tracking: { provider: "native", trackingMoreCarrierCode: "ups" },
    trackingUrl: {
      url: "https://www.ups.com/track?loc={locale}&tracknum={trackingId}",
      fallbackUrl: "https://www.ups.com/track",
      supportsLocale: true,
      formatLocale: (locale) => `${locale}_${locale.toUpperCase()}`
    },
    matchTrackingNumber: (tn) => /^1Z[0-9A-Z]{16}$/i.test(tn),
    meta: {
      trackingMethod: "API officielle UPS Tracking (OAuth2)"
    },
    credentials: {
      usedFor: ["tracking", "shipment"],
      oauth: true,
      supportsCredentialCheck: true,
      fields: [
        { key: "accountNumber", label: "Account Number", type: "string", required: true }
      ]
    },
    supportsLabelGeneration: true,
    products: [
      // MONDE mesuré par destination : US n=198 méd. 3 p80 6, CA n=45 méd. 3, AU n=30 méd. 3.
      { code: "standard", isPickupPoint: false, name: "UPS Standard", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 3, padding: 3 } } },
      { code: "saver", isPickupPoint: false, name: "UPS Saver", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 4, padding: 2 } } },
      // Même produit physique que `standard` ci-dessus (même nom) : valeurs alignées,
      // sinon deux marchands reçoivent des EDD écartées de 3 jours selon le code
      // posé à l'onboarding.
      { code: "ups_standard", isPickupPoint: false, name: "UPS Standard", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "11" }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 3, padding: 3 } } },
      { code: "ups_express", isPickupPoint: false, name: "UPS Express", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "07" }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 3, padding: 2 } } },
      { code: "ups_express_saver", isPickupPoint: false, name: "UPS Express Saver", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "65" }, servedZones: ["FR_METRO", "EU", "MONDE"], zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 4, padding: 2 } } },
      { code: "ups_access_point", isPickupPoint: true, name: "UPS Access Point", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["FR_METRO", "EU"] },
      { code: "ups_expedited", isPickupPoint: false, name: "UPS Expedited", defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: WEEKDAYS }, carrierProductCodes: { default: "08" }, servedZones: ["EU", "MONDE"], zoneDelays: { MONDE: { promise: 6, padding: 2 } } }
    ]
  },
  gls: {
    name: "GLS",
    logo: "gls.png",
    match: (code) => code === "gls",
    tracking: { provider: "native", trackingMoreCarrierCode: "gls" },
    trackingUrl: {
      url: "https://gls-group.com/GROUP/en/parcel-tracking?match={trackingId}",
      fallbackUrl: "https://gls-group.com/GROUP/en/parcel-tracking"
    },
    meta: {
      trackingMethod: "API publique non-authentifi\xE9e (gls-group.com)",
      notes: "Deux endpoints : rstt029 (overview) puis rstt028 (d\xE9tail si code postal disponible)."
    },
    products: [
      { code: "gls", isPickupPoint: false, name: "GLS Standard", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 3, padding: 2 } } },
      { code: "gls-point-relais", isPickupPoint: true, name: "GLS Point Relais", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5 } } },
      { code: "gls_business_parcel", isPickupPoint: false, name: "GLS Business Parcel", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5, padding: 2 } } },
      { code: "gls_europlus", isPickupPoint: false, name: "GLS EuroPlus", defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      // FR n=364 méd. 2 p80 3 ; ES n=64 méd. 3 p80 4 ; DE n=53 méd. 4 p80 4.
      { code: "gls_shop_delivery", isPickupPoint: true, name: "GLS Shop Delivery", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 3, padding: 2 } } },
      { code: "gls_parcel_shop", isPickupPoint: true, name: "GLS ParcelShop", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5 } } }
    ]
  },
  "mondial-relay": {
    name: "Mondial Relay",
    logo: "mondial-relay.png",
    match: (code) => code === "mondial-relay" || code === "mondialrelay",
    tracking: { provider: "native", trackingMoreCarrierCode: "mondialrelay" },
    trackingUrl: {
      url: "https://www.mondialrelay.fr/suivi-de-colis?NumeroExpedition={trackingId}&CodePostal={zipCode}",
      fallbackUrl: "https://www.mondialrelay.fr/suivi-de-colis",
      requiredFields: ["zipCode"]
    },
    meta: {
      trackingMethod: "API web mondialrelay.fr (via proxy SmartProxy)",
      notes: "Authentification par cookie obtenu via scraping. N\xE9cessite proxy r\xE9sidentiel FR."
    },
    credentials: {
      usedFor: ["tracking"],
      fields: [
        { key: "brand", label: "Enseigne (Brand)", type: "string", required: true },
        { key: "securityKey", label: "Security Key", type: "string", required: true, sensitive: true }
      ],
      supportsCredentialCheck: true
    },
    // Two known cart customAttribute conventions for MR pickup points:
    // - `Mondial_Relay_*` (Bellerose / Checkout Blocks integrations)
    // - `PickupPoint*` (Globe app / generic). Note: `PickupPointCourier` is
    //   misnamed by the Globe app — it actually carries an ISO country code
    //   (e.g. "FR", "BE"), not a carrier identifier.
    pickupPointCartAttributes: {
      id: ["Mondial_Relay_CODE", "PickupPointId"],
      zip: ["Mondial_Relay_ZIP_CODE", "PickupPointPostCode"],
      name: ["Mondial_Relay_NAME", "PickupPointName", "PickupPointPlace"],
      address1: ["Mondial_Relay_ADDRESS_1", "PickupPointAddress"],
      city: ["Mondial_Relay_CITY", "PickupPointCity"],
      country: ["Mondial_Relay_COUNTRY", "PickupPointCourier"]
    },
    // 98 % of Mondial Relay parcels are measured as pickup deliveries, so an
    // opaque label like `MREL_SE` must not default to home.
    defaultProductCode: "mondial-relay",
    products: [
      // Même produit physique que `mondial_relay_point_relais` (même nom) : EU aligné
      // sur la mesure (n=606, méd. 3).
      { code: "mondial-relay", isPickupPoint: true, name: "Mondial Relay Point Relais", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: MONDIAL_DAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 3 } } },
      { code: "mondial-relay-home", isPickupPoint: false, name: "Mondial Relay Domicile", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: MONDIAL_DAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5 } } },
      { code: "mondial_relay_point_relais", isPickupPoint: true, name: "Mondial Relay Point Relais", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: MONDIAL_DAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 3 } } },
      // Relais transfrontalier, bien plus rapide que le relais domestique : 93 % du
      // volume est Belgique. Mesuré sur 884 colis livrés (120 j) : EU n=611 méd. 1,5
      // P90 4,5 ; FR n=273 méd. 1,5 P90 4,5. Les deux zones donnent la même paire,
      // d'où l'absence de `zoneDelays`. Servi (avec le supplément de repli) 1 à 5 j,
      // contre 3 à 7 avant cette entrée, ce qui livrait 91 % des colis AVANT l'edd_min.
      { code: "mondial_relay_point_relais_international", isPickupPoint: true, name: "Mondial Relay Point Relais International", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 2, deliveryDays: MONDIAL_DAYS }, servedZones: ["FR_METRO", "EU"] },
      { code: "mondial_relay_domicile", isPickupPoint: false, name: "Mondial Relay Domicile", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: MONDIAL_DAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5 } } },
      { code: "mondial_relay_xl", isPickupPoint: false, name: "Mondial Relay XL", defaultSmartDelivery: { defaultPromise: 4, defaultPadding: 1, deliveryDays: MONDIAL_DAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 6 } } }
    ]
  },
  fedex: {
    name: "FedEx",
    logo: "fedex.png",
    match: (code) => code === "fedex",
    tracking: { provider: "native", trackingMoreCarrierCode: "fedex" },
    trackingUrl: {
      url: "https://www.fedex.com/fedextrack/?trknbr={trackingId}",
      fallbackUrl: "https://www.fedex.com/fedextrack/"
    },
    meta: {
      trackingMethod: "API officielle FedEx Track (OAuth2)"
    },
    credentials: {
      usedFor: ["tracking"],
      fields: [
        { key: "clientId", label: "Client ID", type: "string", required: true },
        { key: "clientSecret", label: "Client Secret", type: "string", required: true, sensitive: true },
        { key: "accountNumber", label: "Account Number", type: "string", required: true }
      ],
      supportsCredentialCheck: true
    },
    products: [
      { code: "fedex", isPickupPoint: false, name: "FedEx Standard", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"] },
      { code: "fedex_international_priority", isPickupPoint: false, name: "FedEx International Priority", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU", "MONDE"] },
      { code: "fedex_international_economy", isPickupPoint: false, name: "FedEx International Economy", defaultSmartDelivery: { defaultPromise: 5, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU", "MONDE"] },
      { code: "fedex_ground", isPickupPoint: false, name: "FedEx Ground", defaultSmartDelivery: { defaultPromise: 5, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"] },
      { code: "fedex_express_saver", isPickupPoint: false, name: "FedEx Express Saver", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"] }
    ]
  },
  "global-e": {
    name: "Global-E",
    match: (code) => code === "global-e" || code === "globale",
    tracking: { provider: "native" },
    trackingUrl: {
      url: "https://www.global-e.com/Tracking/Tracking?orderid={trackingId}&CountryCode={countryCode}",
      fallbackUrl: "https://www.global-e.com/Tracking/Tracking",
      requiredFields: ["countryCode"],
      supportsLocale: true,
      formatCountryCode: (countryCode, locale) => `${countryCode.toLowerCase()}-${locale}`
    },
    meta: {
      trackingMethod: "API officielle Global-E (SDK client)",
      notes: "Utilise le package @global-e/client. Fallback vers TrackShip si configur\xE9."
    },
    credentials: {
      usedFor: ["tracking"],
      fields: [
        { key: "account", label: "Merchant GUID", type: "string", required: true }
      ],
      supportsCredentialCheck: true
    },
    // `ShipperName` from the Global-e API, stored as `carrierDisplayName`. This is
    // Global-e's whole vocabulary: 7 values over a year of production, 5 once the
    // "api" channel variant is normalized away. Unknown values are logged.
    sourceProductMap: {
      "dhl express worldwide": { carrier: "dhl", product: "dhl_express_worldwide" },
      // No exact code for the road offering: `dhl_euroconnect` (promise 3) matches
      // the measured 3-day median, `dhl_economy_select` (promise 5) does not.
      "dhl european road economy": { carrier: "dhl", product: "dhl_euroconnect" },
      "dhl european road": { carrier: "dhl", product: "dhl_euroconnect" },
      "fedex ficp worldwide": { carrier: "fedex", product: "fedex_international_priority" },
      "chronopost standard": { carrier: "chronopost", product: "chronopost-13" }
    },
    products: [
      { code: "global-e", isPickupPoint: false, name: "Global-E", defaultSmartDelivery: { defaultPromise: 5, defaultPadding: 1, deliveryDays: WEEKDAYS }, zoneDelays: { EU: { promise: 2 }, MONDE: { promise: 3 } }, servedZones: ["EU", "OM1", "OM2", "MONDE"] }
    ]
  },
  bpost: {
    name: "bpost",
    logo: "bpost.png",
    match: (code) => code === "bpost" || code === "bepost" || code === "be-post",
    tracking: { provider: "native", trackingMoreCarrierCode: "belgium-post" },
    trackingUrl: {
      url: "https://track.bpost.cloud/btr/web/#/search?itemCode={trackingId}",
      fallbackUrl: "https://track.bpost.cloud/btr/web/"
    },
    meta: {
      trackingMethod: "API publique non-authentifi\xE9e (track.bpost.cloud)"
    },
    products: [
      // Belgian national operator, not a global integrator: the whole parcel
      // history is 100% Belgium (n=13499 on 2026-09-03, zero FR, zero outside
      // Europe) and the shipping shops route every non-BE destination to a
      // dedicated carrier. Same footprint as its peers (postnl, swiss-post,
      // hermes-de), and the same as the pickup product below, which this used
      // to contradict.
      // The FR_METRO / MONDE zoneDelays below are live, not hypothetical: one
      // shop stores servedZones ["FR_METRO","EU","MONDE"] today and
      // `getRegistryTransit` reads zoneDelays independently of servedZones, so
      // both entries serve production transit right now. Do not drop them as
      // dead data.
      { code: "bpost", isPickupPoint: false, name: "bpost", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"], zoneDelays: { FR_METRO: { promise: 3 }, MONDE: { promise: 8, padding: 2 } } },
      { code: "bpost-point-relais", isPickupPoint: true, name: "bpost Point Relais", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  custom: {
    name: "Custom",
    match: (code) => code === "custom",
    tracking: { provider: "manual" },
    trackingUrl: { url: null },
    meta: {
      trackingMethod: "Manuel (API emitParcelStatus ou back-office)"
    },
    products: []
  },
  dpd: {
    name: "DPD",
    match: (code) => code === "dpd" || code === "fake-dpd",
    tracking: { provider: "native", trackingMoreCarrierCode: "dpd" },
    trackingUrl: {
      url: "https://trace.dpd.fr/fr/trace/{trackingId}",
      fallbackUrl: "https://trace.dpd.fr/fr/trace"
    },
    credentials: {
      usedFor: ["tracking", "shipment"],
      // DPD only answers tracking/shipment API calls coming from whitelisted IPs.
      ipWhitelist: ["171.33.105.206", "171.33.92.211"],
      fields: [
        { key: "userid", label: "User ID", type: "string", required: true },
        { key: "password", label: "Password", type: "string", required: true, sensitive: true },
        { key: "customerCountryCode", label: "Customer Country Code", type: "string", required: true },
        { key: "customerCenterNumber", label: "Customer Center Number", type: "string", required: true },
        { key: "customerNumber", label: "Customer Number", type: "string", required: true }
      ],
      supportsCredentialCheck: true
    },
    products: [
      // EU : mesure instable selon la fenêtre (3+2 sur 120 j, 5+1 sur 90 j), on retient la plus large.
      { code: "dpd", isPickupPoint: false, name: "DPD Standard", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 3, padding: 2 } } },
      { code: "dpd-pickup", isPickupPoint: true, name: "DPD Pickup", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["FR_METRO", "EU"], zoneDelays: { EU: { promise: 5 } } }
    ]
  },
  "dpd-uk": {
    name: "DPD UK",
    match: (code) => code === "dpd-uk" || code === "dpducluk" || code === "dpd-ucl-uk",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "dpd-uk" },
    trackingUrl: {
      url: "https://www.dpd.co.uk/service/tracking?consignment={trackingId}",
      fallbackUrl: "https://www.dpd.co.uk/service/tracking"
    },
    products: [
      { code: "dpd-uk", isPickupPoint: false, name: "DPD UK", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "dpd-uk-pickup", isPickupPoint: true, name: "DPD UK Pickup", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  tnt: {
    name: "TNT",
    match: (code) => code === "tnt",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "tnt" },
    trackingUrl: {
      url: "https://www.tnt.com/express/{locale}/site/shipping-tools/tracking.html?searchType=con&cons={trackingId}",
      fallbackUrl: "https://www.tnt.com/express/fr_fr/site/shipping-tools/tracking.html",
      supportsLocale: true,
      formatLocale: (locale) => `${locale}_${locale}`
    },
    products: [
      { code: "tnt", isPickupPoint: false, name: "TNT", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU", "MONDE"] }
    ]
  },
  canadapost: {
    name: "Canada Post",
    match: (code) => code === "canadapost" || code === "canada-post",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "canada-post" },
    trackingUrl: {
      url: "https://www.canadapost-postescanada.ca/track-reperage/{locale}#/details/{trackingId}",
      fallbackUrl: "https://www.canadapost-postescanada.ca/track-reperage/en",
      supportsLocale: true
    },
    products: [
      { code: "canadapost", isPickupPoint: false, name: "Canada Post", defaultSmartDelivery: { defaultPromise: 5, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["MONDE"] }
    ]
  },
  geodis: {
    name: "Geodis",
    logo: "geodis.png",
    match: (code) => code === "geodis",
    tracking: { provider: "native", trackingMoreCarrierCode: "geodis" },
    trackingUrl: {
      url: "https://espace-client.geodis.com/services/destinataires/#/en/suivi/{trackingId}"
    },
    meta: {
      trackingMethod: "API publique non-authentifi\xE9e (endpoint anonyme destinataire)",
      notes: "HMAC-SHA256 avec appKey hardcod\xE9e. Derri\xE8re Cloudflare Bot Management \u2014 risque 403 sur volume \xE9lev\xE9."
    },
    products: [
      { code: "geodis", isPickupPoint: false, name: "Geodis", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO", "EU"] }
    ]
  },
  "colis-prive": {
    name: "Colis Priv\xE9",
    match: (code) => code === "colis-prive" || code === "colisprive",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "colis-prive" },
    trackingUrl: {
      url: "https://www.colisprive.com/moncolis/pages/detailColis.aspx?numColis={trackingId}&cp={zipCode}",
      fallbackUrl: "https://www.colisprive.com/moncolis/",
      requiredFields: ["zipCode"]
    },
    products: [
      { code: "colis-prive", isPickupPoint: false, name: "Colis Priv\xE9", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["FR_METRO"] }
    ]
  },
  seur: {
    name: "SEUR",
    match: (code) => code === "seur",
    tracking: { provider: "native", trackingMoreCarrierCode: "international-seur" },
    trackingUrl: {
      url: "https://www.seur.com/livetracking/?segOnlineIdentifier={trackingId}",
      fallbackUrl: "https://www.seur.com/livetracking/"
    },
    meta: {
      trackingMethod: "API officielle SEUR PIC (OAuth2 password grant)",
      notes: "Token obtenu via POST pic_token avec client_id/client_secret + username/password.",
      limitations: "Adresse du point relais non r\xE9cup\xE9rable. Sur une livraison redirig\xE9e en point relais (destinataire absent \u2192 codes LJ105/LI574\u2026), l'API PIC ne renvoie que le CP/ville de destination + les codes d'\xE9v\xE9nements, jamais l'adresse du point. Cette adresse n'existe que sur la page de suivi client (endpoint consultarEnvioLT, derri\xE8re Imperva Incapsula \u2192 non exploitable en server-to-server). Cons\xE9quence : l'email de mise \xE0 disposition ne peut afficher que l'adresse de destination d'origine, pas celle du point relais. Aucune solution tant que SEUR n'expose pas le pudo dans l'API PIC."
    },
    credentials: {
      usedFor: ["tracking"],
      fields: [
        { key: "clientId", label: "Client ID", type: "string", required: true },
        { key: "clientSecret", label: "Client Secret", type: "string", required: true, sensitive: true },
        { key: "username", label: "Username", type: "string", required: true },
        { key: "password", label: "Password", type: "string", required: true, sensitive: true },
        { key: "accountNumber", label: "Account Number", type: "string", required: true },
        { key: "businessUnit", label: "Business Unit", type: "string", required: true },
        { key: "idNumber", label: "ID Number (NIF)", type: "string", required: true }
      ],
      supportsCredentialCheck: true
    },
    products: [
      { code: "seur", isPickupPoint: false, name: "SEUR", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "seur_24", isPickupPoint: false, name: "SEUR 24", defaultSmartDelivery: { defaultPromise: 1, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "seur_pickup", isPickupPoint: true, name: "SEUR Pickup", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  hermes: {
    name: "Hermes",
    match: (code) => code === "hermes",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "hermes" },
    trackingUrl: {
      url: "https://www.myhermes.de/empfangen/sendungsverfolgung/sendungsinformation/#/{trackingId}",
      fallbackUrl: "https://www.myhermes.de/empfangen/sendungsverfolgung/"
    },
    products: [
      { code: "hermes", isPickupPoint: false, name: "Hermes", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] },
      { code: "hermes-pickup", isPickupPoint: true, name: "Hermes ParcelShop", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  "hermes-de": {
    name: "Hermes Germany",
    match: (code) => code === "hermes-de" || code === "hermesde",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "hermes-de" },
    trackingUrl: {
      url: "https://www.myhermes.de/empfangen/sendungsverfolgung/sendungsinformation/#/{trackingId}",
      fallbackUrl: "https://www.myhermes.de/empfangen/sendungsverfolgung/"
    },
    products: [
      { code: "hermes-de", isPickupPoint: false, name: "Hermes Germany", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] },
      { code: "hermes-de-pickup", isPickupPoint: true, name: "Hermes Germany PaketShop", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  "hermes-uk": {
    name: "Hermes UK (Evri)",
    match: (code) => code === "hermes-uk" || code === "hermesuk" || code === "evri",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "hermes-uk" },
    trackingUrl: {
      url: "https://www.evri.com/track-a-parcel/{trackingId}",
      fallbackUrl: "https://www.evri.com/track-a-parcel"
    },
    products: [
      { code: "hermes-uk", isPickupPoint: false, name: "Hermes UK (Evri)", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] },
      { code: "hermes-uk-pickup", isPickupPoint: true, name: "Evri ParcelShop", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  "swiss-post": {
    name: "Swiss Post",
    logo: "swiss-post.png",
    match: (code) => code === "swiss-post" || code === "swisspost",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "swiss-post" },
    trackingUrl: {
      url: "https://service.post.ch/ekp-web/ui/entry/search/{trackingId}",
      fallbackUrl: "https://www.swisspost.ch/swisspost-tracking"
    },
    meta: {
      trackingMethod: "Via TrackingMore (API universelle)",
      notes: "Impl\xE9mentation API native (developer.post.ch OAuth2) \xE0 pr\xE9voir pour volume \xE9lev\xE9."
    },
    products: [
      { code: "swiss-post", isPickupPoint: false, name: "Swiss Post", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] },
      { code: "swiss-post-pickup", isPickupPoint: true, name: "Swiss Post Pickup", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  postnl: {
    name: "PostNL",
    logo: "postnl.png",
    match: (code) => code === "postnl" || code === "post-nl" || code === "post_nl",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "postnl-3s" },
    trackingUrl: {
      url: "https://jouw.postnl.nl/track-and-trace/{trackingId}/{countryCode}/{zipCode}",
      fallbackUrl: "https://jouw.postnl.nl/track-and-trace/",
      requiredFields: ["zipCode", "countryCode"]
    },
    meta: {
      trackingMethod: "Via TrackingMore (API universelle)",
      notes: "Impl\xE9mentation API native (developer.postnl.nl \u2014 Shipping Status) possible plus tard avec credentials par shop (customercode + customernumber + API key via Mijn PostNL Zakelijk), m\xEAme mod\xE8le que UPS/FedEx."
    },
    products: [
      { code: "postnl", isPickupPoint: false, name: "PostNL", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] },
      { code: "postnl-pickup", isPickupPoint: true, name: "PostNL Pakketpunt", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  sending: {
    name: "Sending",
    logo: "sending.png",
    match: (code) => code === "sending" || code === "sending-es",
    tracking: { provider: "tracking-more", trackingMoreCarrierCode: "sending" },
    trackingUrl: {
      url: null,
      fallbackUrl: "https://www.sending.es/en/find-your-shipment/"
    },
    meta: {
      trackingMethod: "Via TrackingMore (API universelle)",
      notes: "Sending Transporte Urgente \u2014 transporteur espagnol (ES/PT/Andorre/Canaries/Bal\xE9ares). Pas de developer portal public ; API native en contrat B2B direct uniquement."
    },
    products: [
      { code: "sending", isPickupPoint: false, name: "Sending", defaultSmartDelivery: { defaultPromise: 2, defaultPadding: 1, deliveryDays: WEEKDAYS }, servedZones: ["EU"] },
      { code: "sending-pickup", isPickupPoint: true, name: "Sending Pickup", defaultSmartDelivery: { defaultPromise: 3, defaultPadding: 1, deliveryDays: WEEKDAYS_SAT }, servedZones: ["EU"] }
    ]
  },
  // Carriers with tracking URL only (no tracking provider in our system)
  "australia-post": {
    name: "Australia Post",
    match: (code) => code === "australia-post" || code === "australiapost",
    tracking: { provider: "none" },
    trackingUrl: {
      url: "https://auspost.com.au/track/track-details?id={trackingId}",
      fallbackUrl: "https://auspost.com.au/track/"
    },
    products: []
  },
  // Carriers with no tracking URL
  "la-poste": {
    name: "La Poste",
    match: () => false,
    tracking: { provider: "none" },
    products: []
  },
  "customer-carrier": {
    name: "Customer Carrier",
    match: (code) => code === "customer-carrier",
    tracking: { provider: "none" },
    products: []
  },
  "merchant-carrier": {
    name: "Merchant Carrier",
    match: (code) => code === "merchant-carrier",
    tracking: { provider: "none" },
    products: []
  },
  logfret: {
    name: "LogFret",
    match: (code) => code === "logfret",
    tracking: { provider: "none" },
    products: []
  },
  crosslog: {
    name: "Crosslog",
    match: (code) => code === "crosslog",
    tracking: { provider: "none" },
    products: []
  },
  malca: {
    name: "Malca",
    match: (code) => code === "malca",
    tracking: { provider: "none" },
    products: []
  },
  envoidunet: {
    name: "EnvoiDunet",
    match: (code) => code === "envoidunet",
    tracking: { provider: "none" },
    products: []
  },
  bleckmann: {
    name: "Bleckmann",
    match: (code) => code === "bleckmann",
    tracking: { provider: "none" },
    products: []
  },
  sympl: {
    name: "Sympl",
    match: (code) => code === "sympl",
    tracking: { provider: "none" },
    products: []
  },
  "api-carrier": {
    name: "API Carrier",
    match: (code) => code === "api-carrier",
    tracking: { provider: "none" },
    products: []
  }
};
var registryEntries = Object.entries(CARRIER_REGISTRY);
registryEntries.filter(([, def]) => def.tracking.provider !== "none").map(([code]) => code);
var VARIANT_NAMES = {
  "la-poste-colissimo": "Colissimo",
  "colissimo/colissimo-livraison-a-domicile-avec-signature": "Colissimo",
  colissimov2: "Colissimo",
  "chronopost-18": "Chronopost 18",
  "chrono-relais": "Chrono Relais",
  "chrono-shop2shop": "Chrono Shop2Shop",
  "fake-dpd": "Fake DPD",
  dpdUclUk: "DPD UCL UK",
  "dhl-express": "DHL Express",
  "dhl-economy-select": "DHL Economy Select",
  "dhl-tax-free": "DHL Tax Free",
  "ups-standard": "UPS Standard",
  "ups-saver": "UPS Saver",
  "ups-ground": "UPS Ground"
};
var CARRIERS_NAMES = {
  ...Object.fromEntries(registryEntries.map(([code, def]) => [code, def.name])),
  ...VARIANT_NAMES
};
var NON_BRAND_CARRIER_CODES = /* @__PURE__ */ new Set([
  "custom",
  "customer-carrier",
  "merchant-carrier",
  "api-carrier"
]);
registryEntries.filter(([code]) => !NON_BRAND_CARRIER_CODES.has(code)).map(([, def]) => ({
  name: def.name,
  needle: `-${normalizeShippingLine(def.name)}-`
})).sort((a, b) => b.needle.length - a.needle.length);
function normalizeShippingLine(input) {
  return input.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
var FR_METRO_COUNTRIES = ["FR", "MC"];
var OM1_COUNTRIES = ["GP", "MQ", "GF", "RE", "YT", "BL", "MF", "PM"];
var OM2_COUNTRIES = ["NC", "PF", "WF", "TF"];
var EU_COUNTRIES = [
  // EU member states (FR is FR_METRO)
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA + Switzerland + UK + European microstates
  "IS",
  "LI",
  "NO",
  "CH",
  "GB",
  "AD",
  "SM",
  "VA",
  "GI"
];
({
  ...Object.fromEntries(FR_METRO_COUNTRIES.map((c) => [c, "FR_METRO"])),
  ...Object.fromEntries(OM1_COUNTRIES.map((c) => [c, "OM1"])),
  ...Object.fromEntries(OM2_COUNTRIES.map((c) => [c, "OM2"])),
  ...Object.fromEntries(EU_COUNTRIES.map((c) => [c, "EU"]))
});
(() => {
  const result = {};
  for (const [code, def] of Object.entries(CARRIER_REGISTRY)) {
    const creds = def.credentials;
    if (creds) {
      result[code] = {
        usedFor: creds.usedFor,
        fields: creds.fields,
        oauth: creds.oauth,
        ipWhitelist: creds.ipWhitelist,
        supportsCredentialCheck: creds.supportsCredentialCheck
      };
    }
  }
  return result;
})();

// src/registry.ts
var VARIABLE_REGISTRY = {
  "shop.name": {
    id: "shop.name",
    name: "Nom du shop",
    description: "Nom de la boutique",
    type: "string",
    group: "shop",
    requires: ["shop"],
    exampleValue: "Ma Boutique",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.shop?.name ?? null,
    setter: (ctx, value) => {
      if (ctx.shop && typeof value === "string") {
        ctx.shop.name = value;
      }
    }
  },
  "parcel.trackingId": {
    id: "parcel.trackingId",
    name: "Num\xE9ro de suivi",
    description: "Identifiant de suivi du colis",
    type: "string",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: "TRK123456",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.trackingId ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel && typeof value === "string") {
        ctx.parcel.trackingId = value;
      }
    }
  },
  "parcel.trackingNumber": {
    id: "parcel.trackingNumber",
    name: "Num\xE9ro de tracking",
    description: "Num\xE9ro de tracking du colis",
    type: "string",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: "TRK123456",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => {
      if (ctx.parcel && "trackingNumbers" in ctx.parcel) {
        return ctx.parcel.trackingNumbers?.[0] ?? null;
      }
      return ctx.parcel?.trackingId ?? null;
    }
  },
  "parcel.orderNumber": {
    id: "parcel.orderNumber",
    name: "Num\xE9ro de commande",
    description: "Num\xE9ro de commande associ\xE9 au colis",
    type: "string",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: "ORD-1001",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.orderNumber ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel && typeof value === "string") {
        ctx.parcel.orderNumber = value;
      }
    }
  },
  "parcel.carrierCode": {
    id: "parcel.carrierCode",
    name: "Code transporteur",
    description: "Code du transporteur",
    type: "string",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: "UPS",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.carrierCode ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel && typeof value === "string") {
        ctx.parcel.carrierCode = value;
      }
      if (typeof value === "string") {
        ctx.carrierCode = value;
      }
    }
  },
  "parcel.status": {
    id: "parcel.status",
    name: "Statut du colis",
    description: "Statut actuel du colis",
    type: "string",
    group: "parcel",
    requires: ["parcel", "status"],
    exampleValue: "IN_TRANSIT",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => {
      if (ctx.parcel && "status" in ctx.parcel) {
        return ctx.parcel.status ?? null;
      }
      return ctx.status ?? null;
    }
  },
  "parcel.customerLocale": {
    id: "parcel.customerLocale",
    name: "Langue client",
    description: "Langue du client (fr, en, es, de)",
    type: "string",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: "fr",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => {
      if (ctx.parcel && "customerLocale" in ctx.parcel) {
        return ctx.parcel.customerLocale ?? null;
      }
      return null;
    },
    setter: (ctx, value) => {
      if (ctx.parcel && "customerLocale" in ctx.parcel && typeof value === "string") {
        ctx.parcel.customerLocale = value;
      }
    }
  },
  "parcel.trackingUrl": {
    id: "parcel.trackingUrl",
    name: "URL de suivi",
    description: "L'URL de la page de suivi de la commande",
    type: "string",
    group: "parcel",
    requires: ["trackingUrl"],
    exampleValue: "https://track.baback.co/tracking/TRK123456",
    availableIn: ["template"],
    editable: false,
    resolver: (ctx) => {
      return ctx.trackingUrl ?? null;
    }
  },
  "parcel.reviewUrl": {
    id: "parcel.reviewUrl",
    name: "URL de notation",
    description: "L'URL de la page de notation de la commande",
    type: "string",
    group: "parcel",
    requires: ["reviewUrl"],
    exampleValue: "https://track.baback.co/tracking/shops/shop-1/rate/token",
    availableIn: ["template"],
    editable: false,
    resolver: (ctx) => {
      return ctx.reviewUrl ?? null;
    }
  },
  "links.giftConfig": {
    id: "links.giftConfig",
    name: "Lien page cadeau",
    description: "Lien vers la page de configuration des cadeaux",
    type: "string",
    group: "links",
    requires: ["links"],
    exampleValue: "#",
    availableIn: ["template"],
    editable: false,
    resolver: (ctx) => ctx.links?.giftConfig ?? null
  },
  "links.trackingPage": {
    id: "links.trackingPage",
    name: "Lien page de suivi",
    description: "Lien vers la page de suivi publique de la commande",
    type: "string",
    group: "links",
    requires: ["links"],
    exampleValue: "#",
    availableIn: ["template"],
    editable: false,
    deprecated: true,
    resolver: (ctx) => ctx.links?.trackingPage ?? null
  },
  "customer.firstName": {
    id: "customer.firstName",
    name: "Pr\xE9nom du client",
    description: "Pr\xE9nom du destinataire du colis",
    type: "string",
    group: "customer",
    requires: ["parcel"],
    exampleValue: "Raphael",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.firstName ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.firstName = value;
        ctx.parcel.destination.name = `${value} ${ctx.parcel.destination.lastName || ""}`.trim();
      }
    }
  },
  "customer.lastName": {
    id: "customer.lastName",
    name: "Nom du client",
    description: "Nom du destinataire du colis",
    type: "string",
    group: "customer",
    requires: ["parcel"],
    exampleValue: "Martin",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.lastName ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.lastName = value;
        ctx.parcel.destination.name = `${ctx.parcel.destination.firstName || ""} ${value}`.trim();
      }
    }
  },
  "customer.email": {
    id: "customer.email",
    name: "Email du client",
    description: "Email du destinataire du colis",
    type: "string",
    group: "customer",
    requires: ["parcel"],
    exampleValue: "raphael@baback.co",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => {
      if (ctx.parcel && "customerEmail" in ctx.parcel) {
        return ctx.parcel.customerEmail ?? null;
      }
      return null;
    },
    setter: (ctx, value) => {
      if (ctx.parcel && "customerEmail" in ctx.parcel && typeof value === "string") {
        ctx.parcel.customerEmail = value;
      }
    }
  },
  "address.name": {
    id: "address.name",
    name: "Nom de la destination",
    description: "Nom de la destination (nom du client \xE0 domicile, ou nom du point relais)",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "La Poste LEVALLOIS PERRET WILSON",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.parcel?.destination?.name ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.name = value;
      }
    }
  },
  "address.address1": {
    id: "address.address1",
    name: "Adresse ligne 1",
    description: "Premi\xE8re ligne de l'adresse du client",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "123 rue de la Paix",
    availableIn: ["template", "workflow"],
    editable: true,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.address1 = value;
      }
    },
    resolver: (ctx) => ctx.parcel?.destination?.address1 ?? null
  },
  "address.address2": {
    id: "address.address2",
    name: "Adresse ligne 2",
    description: "Deuxi\xE8me ligne de l'adresse du client",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "Appartement 4B",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.address2 ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.address2 = value;
      }
    }
  },
  "address.zip": {
    id: "address.zip",
    name: "Code postal",
    description: "Code postal du client",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "75001",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.zip ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.zip = value;
      }
    }
  },
  "address.city": {
    id: "address.city",
    name: "Ville",
    description: "Ville du client",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "Paris",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.city ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.city = value;
      }
    }
  },
  "address.country": {
    id: "address.country",
    name: "Pays",
    description: "Pays du client",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "France",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.country ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.country = value;
      }
    }
  },
  "address.countryCode": {
    id: "address.countryCode",
    name: "Code pays",
    description: "Code pays du client (FR, US, etc.)",
    type: "string",
    group: "address",
    requires: ["parcel"],
    exampleValue: "FR",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.countryCode ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.countryCode = value;
      }
    }
  },
  "address.isPickupPoint": {
    id: "address.isPickupPoint",
    name: "Point relais",
    description: "D\xE9finit si l'adresse est un point de retrait",
    type: "boolean",
    group: "address",
    requires: ["parcel"],
    exampleValue: false,
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.parcel?.destination?.isPickupPoint ?? false,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "boolean") {
        ctx.parcel.destination.isPickupPoint = value;
      }
    }
  },
  "carrier.name": {
    id: "carrier.name",
    name: "Nom du transporteur",
    description: "Nom du transporteur",
    type: "string",
    group: "carrier",
    requires: ["carrierCode"],
    exampleValue: "UPS",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => {
      if (!ctx.carrierCode) return null;
      const displayName = ctx.parcel?.carrierDisplayName;
      if (displayName) return displayName;
      return CARRIERS_NAMES[ctx.carrierCode] ?? ctx.carrierCode;
    },
    setter: (ctx, value) => {
      if (typeof value !== "string") return;
      const carrierEntry = Object.entries(CARRIERS_NAMES).find(
        ([, name]) => name.toLowerCase() === value.toLowerCase()
      );
      if (carrierEntry) {
        ctx.carrierCode = carrierEntry[0];
        if (ctx.parcel) {
          ctx.parcel.carrierCode = carrierEntry[0];
          ctx.parcel.carrierDisplayName = null;
        }
      } else {
        ctx.carrierCode = value;
        if (ctx.parcel) {
          ctx.parcel.carrierCode = value;
          ctx.parcel.carrierDisplayName = null;
        }
      }
    }
  },
  "pickupPoint.name": {
    id: "pickupPoint.name",
    name: "Nom du point relais",
    description: "Nom du point de retrait",
    type: "string",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: "Relais Colis Paris",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.pickupPoint?.name ?? null,
    setter: (ctx, value) => {
      if (ctx.pickupPoint && typeof value === "string") {
        ctx.pickupPoint.name = value;
      }
    }
  },
  "pickupPoint.addressLine1": {
    id: "pickupPoint.addressLine1",
    name: "PR : Adresse ligne 1",
    description: "Premi\xE8re ligne de l'adresse du point relais",
    type: "string",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: "123 rue de la Paix",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.pickupPoint?.addressLine1 ?? null,
    setter: (ctx, value) => {
      if (ctx.pickupPoint && typeof value === "string") {
        ctx.pickupPoint.addressLine1 = value;
      }
    }
  },
  "pickupPoint.addressLine2": {
    id: "pickupPoint.addressLine2",
    name: "PR : Adresse ligne 2",
    description: "Deuxi\xE8me ligne de l'adresse du point relais",
    type: "string",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: "",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.pickupPoint?.addressLine2 ?? null,
    setter: (ctx, value) => {
      if (ctx.pickupPoint && typeof value === "string") {
        ctx.pickupPoint.addressLine2 = value;
      }
    }
  },
  "pickupPoint.postalCode": {
    id: "pickupPoint.postalCode",
    name: "PR : Code postal",
    description: "Code postal du point relais",
    type: "string",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: "75001",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.pickupPoint?.postalCode ?? null,
    setter: (ctx, value) => {
      if (ctx.pickupPoint && typeof value === "string") {
        ctx.pickupPoint.postalCode = value;
      }
    }
  },
  "pickupPoint.city": {
    id: "pickupPoint.city",
    name: "PR : Ville",
    description: "Ville du point relais",
    type: "string",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: "Paris",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.pickupPoint?.city ?? null,
    setter: (ctx, value) => {
      if (ctx.pickupPoint && typeof value === "string") {
        ctx.pickupPoint.city = value;
      }
    }
  },
  "pickupPoint.countryCode": {
    id: "pickupPoint.countryCode",
    name: "PR : Code pays",
    description: "Code pays du point relais (FR, US, etc.)",
    type: "string",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: "FR",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.pickupPoint?.countryCode ?? null,
    setter: (ctx, value) => {
      if (ctx.pickupPoint && typeof value === "string") {
        ctx.pickupPoint.countryCode = value;
      }
    }
  },
  "wallet.appleUrl": {
    id: "wallet.appleUrl",
    name: "URL Apple Wallet",
    description: "URL pour ajouter au wallet Apple",
    type: "string",
    group: "wallet",
    requires: ["appleWalletURL"],
    exampleValue: "https://wallet.apple.com/...",
    availableIn: ["template"],
    editable: false,
    resolver: (ctx) => ctx.appleWalletURL ?? null
  },
  "wallet.googleUrl": {
    id: "wallet.googleUrl",
    name: "URL Google Wallet",
    description: "URL pour ajouter au wallet Google",
    type: "string",
    group: "wallet",
    requires: ["googleWalletURL"],
    exampleValue: "https://pay.google.com/...",
    availableIn: ["template"],
    editable: false,
    resolver: (ctx) => ctx.googleWalletURL ?? null
  },
  "order.id": {
    id: "order.id",
    name: "Order ID",
    description: "L'ID de la commande",
    type: "string",
    group: "order",
    requires: ["parcel"],
    exampleValue: "324495988654",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.orderNumber ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel && typeof value === "string") {
        ctx.parcel.orderNumber = value;
      }
    }
  },
  "order.firstName": {
    id: "order.firstName",
    name: "Firstname",
    description: "Le pr\xE9nom du client qui a pass\xE9 la commande",
    type: "string",
    group: "order",
    requires: ["parcel"],
    exampleValue: "Raphael",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.firstName ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.firstName = value;
        ctx.parcel.destination.name = `${value} ${ctx.parcel.destination.lastName || ""}`.trim();
      }
    }
  },
  "order.lastName": {
    id: "order.lastName",
    name: "Lastname",
    description: "Le nom du client qui a pass\xE9 la commande",
    type: "string",
    group: "order",
    requires: ["parcel"],
    exampleValue: "Martin",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => ctx.parcel?.destination?.lastName ?? null,
    setter: (ctx, value) => {
      if (ctx.parcel?.destination && typeof value === "string") {
        ctx.parcel.destination.lastName = value;
        ctx.parcel.destination.name = `${ctx.parcel.destination.firstName || ""} ${value}`.trim();
      }
    }
  },
  "order.email": {
    id: "order.email",
    name: "Email",
    description: "L'email du client qui a pass\xE9 la commande",
    type: "string",
    group: "order",
    requires: ["parcel"],
    exampleValue: "raphael@baback.co",
    availableIn: ["template", "workflow"],
    editable: true,
    resolver: (ctx) => {
      if (ctx.parcel && "customerEmail" in ctx.parcel) {
        return ctx.parcel.customerEmail ?? null;
      }
      return null;
    },
    setter: (ctx, value) => {
      if (ctx.parcel && "customerEmail" in ctx.parcel && typeof value === "string") {
        ctx.parcel.customerEmail = value;
      }
    }
  },
  "parcel.lineItems": {
    id: "parcel.lineItems",
    name: "Articles du colis",
    description: "Liste des articles contenus dans le colis",
    type: "array",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: [
      {
        name: "Produit exemple",
        quantity: 1,
        price: 29.99,
        sku: "SKU123",
        imageUrl: "https://via.placeholder.com/150"
      }
    ],
    availableIn: ["template"],
    editable: false,
    injectableInText: false,
    resolver: (ctx) => {
      if (ctx.parcel && "lineItems" in ctx.parcel) {
        return ctx.parcel.lineItems ?? null;
      }
      return null;
    }
  },
  "parcel.trackingEvents": {
    id: "parcel.trackingEvents",
    name: "Historique de suivi",
    description: "Liste des \xE9v\xE9nements de suivi du colis",
    type: "array",
    group: "parcel",
    requires: ["parcel"],
    exampleValue: [],
    availableIn: ["template"],
    editable: false,
    injectableInText: false,
    resolver: (ctx) => {
      if (ctx.parcel && "events" in ctx.parcel) {
        return ctx.parcel.events ?? null;
      }
      return null;
    }
  },
  "pickupPoint.openingHours": {
    id: "pickupPoint.openingHours",
    name: "Horaires d'ouverture",
    description: "Horaires d'ouverture du point relais",
    type: "object",
    group: "pickupPoint",
    requires: ["pickupPoint"],
    exampleValue: {
      friday: [{ from: "08h", to: "18h" }],
      monday: [{ from: "08h", to: "18h" }],
      saturday: [{ from: "08h", to: "18h" }],
      sunday: [],
      thursday: [{ from: "08h", to: "18h" }],
      tuesday: [{ from: "08h", to: "18h" }],
      wednesday: [{ from: "08h", to: "18h" }]
    },
    availableIn: ["template"],
    editable: false,
    injectableInText: false,
    resolver: (ctx) => {
      if (ctx.pickupPoint && "openingHours" in ctx.pickupPoint) {
        return ctx.pickupPoint.openingHours ?? null;
      }
      return null;
    }
  },
  // Claim variables
  "claim.status": {
    id: "claim.status",
    name: "Statut du litige",
    description: "Statut actuel du litige (NEW, IN_PROGRESS, RESOLVED, REJECTED, CLOSED)",
    type: "string",
    group: "claim",
    requires: ["claim"],
    exampleValue: "IN_PROGRESS",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.claim?.status ?? null
  },
  "claim.previousStatus": {
    id: "claim.previousStatus",
    name: "Statut pr\xE9c\xE9dent du litige",
    description: "Statut du litige avant le changement",
    type: "string",
    group: "claim",
    requires: ["claim"],
    exampleValue: "NEW",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.claim?.previousStatus ?? null
  },
  "claim.type": {
    id: "claim.type",
    name: "Type de litige",
    description: "Type du litige (LOST, DAMAGED, etc.)",
    type: "string",
    group: "claim",
    requires: ["claim"],
    exampleValue: "LOST",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.claim?.type ?? null
  },
  "claim.description": {
    id: "claim.description",
    name: "Description du litige",
    description: "Description d\xE9taill\xE9e du litige",
    type: "string",
    group: "claim",
    requires: ["claim"],
    exampleValue: "Colis perdu en transit",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.claim?.description ?? null
  },
  "claim.externalId": {
    id: "claim.externalId",
    name: "ID externe du litige",
    description: "Identifiant externe du litige (r\xE9f\xE9rence marchande)",
    type: "string",
    group: "claim",
    requires: ["claim"],
    exampleValue: "CLAIM-12345",
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.claim?.externalId ?? null
  },
  "claim.createdAt": {
    id: "claim.createdAt",
    name: "Date de cr\xE9ation du litige",
    description: "Date de cr\xE9ation du litige",
    type: "date",
    group: "claim",
    requires: ["claim"],
    exampleValue: 17e11,
    availableIn: ["template", "workflow"],
    editable: false,
    resolver: (ctx) => ctx.claim?.createdAt ?? null
  }
};
var VARIABLE_MAP = new Map(
  Object.entries(VARIABLE_REGISTRY).map(([id, v]) => [id, v])
);
function getVariableDefinition(id) {
  return VARIABLE_MAP.get(id);
}

// src/resolvers.ts
function defaultFormat(value, type, locale) {
  if (value === null || value === void 0) {
    return "";
  }
  switch (type) {
    case "string":
      return String(value);
    case "number":
      return String(value);
    case "date":
      if (!(value instanceof Date)) {
        return "";
      }
      return value.toLocaleDateString(locale || "fr-FR");
    // TODO I18n support
    case "boolean":
      return value ? "true" : "false";
    case "array":
    case "object":
      console.warn(
        `[variables-registry] Attempting to format complex type "${type}" to string. This should not happen - complex types should have injectableInText: false.`
      );
      return JSON.stringify(value);
    default:
      return String(value);
  }
}
function resolveAndFormatVariables(context, options) {
  const { locale, variableIds } = options || {};
  const idsToResolve = variableIds ?? Array.from(VARIABLE_MAP.keys());
  const result = {};
  for (const id of idsToResolve) {
    const definition = VARIABLE_MAP.get(id);
    if (!definition) {
      console.warn(`[variables-registry] Variable definition not found: ${id}`);
      continue;
    }
    try {
      const resolvedValue = definition.resolver(context);
      const valueToFormat = resolvedValue ?? definition.exampleValue;
      if (definition.injectableInText === false || definition.type === "array" || definition.type === "object") {
        result[id] = valueToFormat;
      } else {
        const formattedValue = definition.format ? definition.format(valueToFormat, locale) : defaultFormat(valueToFormat, definition.type, locale);
        result[id] = formattedValue;
      }
    } catch (error) {
      console.error(
        `[variables-registry] Error resolving variable ${id}:`,
        error
      );
      if (definition.injectableInText === false || definition.type === "array" || definition.type === "object") {
        result[id] = definition.exampleValue;
      } else {
        const defaultFormatted = definition.format ? definition.format(definition.exampleValue, locale) : defaultFormat(definition.exampleValue, definition.type, locale);
        result[id] = defaultFormatted;
      }
    }
  }
  return result;
}

// src/subsets.ts
var TEMPLATE_VARIABLES = Object.values(VARIABLE_REGISTRY).filter(
  (v) => v.availableIn.includes("template")
);
var WORKFLOW_VARIABLES = Object.values(VARIABLE_REGISTRY).filter(
  (v) => v.availableIn.includes("workflow")
);
function getVariablesByGroup(variables) {
  return variables.reduce(
    (acc, variable) => {
      if (!acc[variable.group]) {
        acc[variable.group] = [];
      }
      acc[variable.group].push(variable);
      return acc;
    },
    {}
  );
}
function getActiveVariables(variables) {
  return variables.filter((v) => !v.deprecated);
}

// src/utils.ts
function createValuesRecord(variables) {
  return variables.reduce((acc, variable) => {
    acc[variable.id] = variable.exampleValue;
    return acc;
  }, {});
}

// src/groups.ts
var VARIABLE_GROUPS = {
  shop: {
    icon: "store",
    labelKey: "variableGroups.shop"
  },
  parcel: {
    icon: "package",
    labelKey: "variableGroups.parcel"
  },
  customer: {
    icon: "user",
    labelKey: "variableGroups.customer"
  },
  address: {
    icon: "map-pin",
    labelKey: "variableGroups.address"
  },
  carrier: {
    icon: "truck",
    labelKey: "variableGroups.carrier"
  },
  pickupPoint: {
    icon: "map-pin",
    labelKey: "variableGroups.pickupPoint"
  },
  order: {
    icon: "shopping-cart",
    labelKey: "variableGroups.order"
  },
  wallet: {
    icon: "wallet",
    labelKey: "variableGroups.wallet"
  },
  links: {
    icon: "link",
    labelKey: "variableGroups.links"
  },
  claim: {
    icon: "alert-triangle",
    labelKey: "variableGroups.claim"
  }
};
function resolveVariables(context) {
  const values = {};
  Object.values(VARIABLE_REGISTRY).forEach((variable) => {
    values[variable.id] = variable.resolver(context);
  });
  return values;
}
function createFormatter(values) {
  return (text) => {
    if (!text?.trim().length) return "";
    return text.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, variableId) => {
      const trimmedId = variableId.trim();
      const value = values[trimmedId];
      if (value === void 0 || value === null) {
        console.warn(`[variables-registry] Variable not found: ${trimmedId}`);
        return match;
      }
      const definition = getVariableDefinition(trimmedId);
      if (definition?.injectableInText === false) {
        console.warn(
          `[variables-registry] Variable "${trimmedId}" cannot be interpolated in text (complex type). Use getVariableValue() instead.`
        );
        return match;
      }
      if (typeof value === "object" && value !== null && !(value instanceof Date)) {
        console.warn(
          `[variables-registry] Variable "${trimmedId}" is a complex type and cannot be interpolated in text.`
        );
        return match;
      }
      return String(value);
    });
  };
}
var EMPTY_SET = /* @__PURE__ */ new Set();
var VariablesDictionaryContext = createContext({
  variables: [],
  setVariable: (_path, _value) => {
  },
  unavailableVariableIds: EMPTY_SET
});
var useVariablesDictionary = () => useContext(VariablesDictionaryContext);
var useUnavailableVariableIds = () => useContext(VariablesDictionaryContext).unavailableVariableIds;
function VariablesDictionaryProvider({
  children,
  variablesDefinition = [],
  unavailableVariableIds = EMPTY_SET
}) {
  const [variables, setVariables] = useState(variablesDefinition);
  const setVariable = useCallback((path, value) => {
    setVariables((prev) => {
      const index = variables.findIndex((definition) => definition.id === path);
      const newVariables = [...prev];
      newVariables[index] = { ...prev[index], exampleValue: value };
      return newVariables;
    });
  }, [variables]);
  useEffect(() => {
    localStorage.setItem(
      "notifications-editor-variables",
      JSON.stringify(variables)
    );
  }, [variables]);
  return /* @__PURE__ */ jsx(VariablesDictionaryContext.Provider, { value: { variables, setVariable, unavailableVariableIds }, children });
}
function useVariablesValues() {
  const { variables } = useVariablesDictionary();
  const variablesValues = useMemo(() => {
    return createValuesRecord(variables);
  }, [variables]);
  const getVariableValue = useMemo(
    () => (variableId) => {
      const value = variablesValues[variableId];
      return value !== void 0 ? value : null;
    },
    [variablesValues]
  );
  return { variablesValues, getVariableValue };
}
function createMockContext() {
  const parcel = createMockParcel();
  const shop = createMockShop();
  const pickupPoint = createMockPickupPoint();
  return {
    trackingUrl: "https://tracking.baback.co/TRACK123456",
    reviewUrl: "https://tracking.baback.co/tracking/shops/mock-shop/rate/mock-token",
    links: {
      trackingPage: "https://app.baback.co/track/TRACK123456",
      giftConfig: "https://app.baback.co/gift/mock-order-1"
    },
    parcel,
    shop,
    pickupPoint,
    carrierCode: parcel.carrierCode,
    status: "OUT_FOR_DELIVERY",
    appleWalletURL: "https://wallet.apple.com/mock-pass",
    googleWalletURL: "https://pay.google.com/mock-pass",
    claim: {
      type: "LOST",
      status: "IN_PROGRESS",
      previousStatus: "NEW",
      description: "Colis perdu en transit",
      externalId: "CLAIM-12345",
      createdAt: Date.now() - 864e5,
      updatedAt: Date.now()
    }
  };
}

// src/apply.ts
function applyVariablesToContext(options = {}) {
  const {
    variables = {},
    shop,
    parcel,
    pickupPoint,
    carrierCode,
    status,
    trackingUrl,
    reviewUrl,
    links,
    appleWalletURL,
    googleWalletURL
  } = options;
  const baseContext = createMockContext();
  const context = {
    ...baseContext,
    ...shop && { shop },
    ...parcel && { parcel },
    ...pickupPoint && { pickupPoint },
    ...carrierCode && { carrierCode },
    ...status && { status },
    ...trackingUrl && { trackingUrl },
    ...reviewUrl && { reviewUrl },
    ...links && { links },
    ...appleWalletURL && { appleWalletURL },
    ...googleWalletURL && { googleWalletURL }
  };
  for (const [varId, value] of Object.entries(variables)) {
    const definition = VARIABLE_MAP.get(varId);
    if (!definition) {
      console.warn(
        `[variables-registry] Variable definition not found: ${varId}`
      );
      continue;
    }
    if (!definition.setter) {
      console.warn(
        `[variables-registry] Variable ${varId} does not have a setter and cannot be customized`
      );
      continue;
    }
    try {
      definition.setter(context, value);
    } catch (error) {
      console.error(
        `[variables-registry] Error setting variable ${varId}:`,
        error
      );
    }
  }
  return context;
}

// src/contexts.ts
var VARIABLE_CONTEXTS = {
  parcel_event: {
    id: "parcel_event",
    name: "Parcel Event",
    description: "Full tracking context with parcel, carrier, delivery, and pickup point information",
    provides: ["parcel", "shop", "pickupPoint", "carrierCode", "status", "trackingUrl", "reviewUrl", "appleWalletURL", "googleWalletURL", "links"]
  },
  order_event: {
    id: "order_event",
    name: "Order Event",
    description: "Order-centric context without shipping/carrier details",
    provides: ["shop"]
  },
  customer_event: {
    id: "customer_event",
    name: "Customer Event",
    description: "Minimal context with customer and shop information only",
    provides: ["parcel", "shop"]
  },
  click_and_collect_event: {
    id: "click_and_collect_event",
    name: "Click & Collect Event",
    description: "Order ready for in-store pickup. Provides shop and pickup point (with opening hours and address from the warehouse). A synthetic parcel-like structure is built internally to render the existing template provider, but `parcel` is intentionally NOT declared as provided here: the synthetic parcel exposes fakes for shipping fields (trackingId, carrierCode, events\u2026) that would mislead merchants in the editor. The template default still resolves `{{parcel.orderNumber}}` etc. via the synthetic parcel at render time.",
    provides: ["shop", "pickupPoint"]
  },
  claim_event: {
    id: "claim_event",
    name: "Claim Event",
    description: "Claim status change context with claim, parcel, and order information",
    provides: ["parcel", "shop", "claim"]
  }
};
function getVariablesForContext(contextType) {
  const context = VARIABLE_CONTEXTS[contextType];
  if (!context) {
    return [];
  }
  const providedSources = new Set(context.provides);
  return Object.entries(VARIABLE_REGISTRY).filter(([_, definition]) => {
    return definition.requires.some((source) => providedSources.has(source));
  }).map(([id]) => id);
}
function getDataSourcesForContext(contextType) {
  const context = VARIABLE_CONTEXTS[contextType];
  return context?.provides ?? [];
}
function isVariableAvailableInContext(variableId, contextType) {
  const definition = VARIABLE_REGISTRY[variableId];
  if (!definition) {
    return false;
  }
  const context = VARIABLE_CONTEXTS[contextType];
  if (!context) {
    return false;
  }
  return definition.requires.some((source) => context.provides.includes(source));
}
function getUnavailableVariables(variableIds, contextType) {
  return variableIds.filter((id) => !isVariableAvailableInContext(id, contextType));
}
function getAvailableVariables(variableIds, contextType) {
  return variableIds.filter((id) => isVariableAvailableInContext(id, contextType));
}
var TRIGGER_VARIABLE_CONTEXTS = {
  tracking_status_change: "parcel_event",
  order_not_fulfilled: "order_event",
  order_created: "order_event",
  click_and_collect_status_change: "click_and_collect_event",
  claim_status_change: "claim_event"
};
function getVariablesForTrigger(triggerType) {
  const contextType = TRIGGER_VARIABLE_CONTEXTS[triggerType];
  return getVariablesForContext(contextType);
}

export { TEMPLATE_VARIABLES, TRIGGER_VARIABLE_CONTEXTS, VARIABLE_CONTEXTS, VARIABLE_GROUPS, VARIABLE_MAP, VARIABLE_REGISTRY, VariablesDictionaryProvider, WORKFLOW_VARIABLES, applyVariablesToContext, createFormatter, createMockContext, createValuesRecord, getActiveVariables, getAvailableVariables, getDataSourcesForContext, getUnavailableVariables, getVariableDefinition, getVariablesByGroup, getVariablesForContext, getVariablesForTrigger, isVariableAvailableInContext, resolveAndFormatVariables, resolveVariables, useUnavailableVariableIds, useVariablesDictionary, useVariablesValues };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map